import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Badge } from '../components/ui/badge';
import { Checkbox } from '../components/ui/checkbox';
import { toast } from 'sonner';
import { Eye, Search, RefreshCw, ClipboardList, Download } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_LABELS = {
  draft: { label: 'Draft (Self-Assessment)', class: 'bg-gray-500' },
  published: { label: 'Published', class: 'bg-blue-500' },
  pending_appraiser: { label: 'Pending Manager Review', class: 'bg-amber-500' },
  pending_appraisee_review: { label: 'Pending Employee Review', class: 'bg-indigo-500' },
  pending_hr: { label: 'Pending HR Review', class: 'bg-purple-500' },
  completed: { label: 'Completed', class: 'bg-green-600' },
};

const fmtScore = (v) => (v === null || v === undefined || v === '' ? '—' : `${v}%`);
const fmtDate = (s) => (s ? new Date(s).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—');

const AppraisalReportsAdmin = () => {
  const [fiscalYears, setFiscalYears] = useState({ current: '', options: [] });
  const [fiscalYear, setFiscalYear] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [filterOptions, setFilterOptions] = useState({ departments: [], projects: [] });
  const [departmentFilter, setDepartmentFilter] = useState('all');
  const [projectFilter, setProjectFilter] = useState('all');
  const [users, setUsers] = useState([]);
  const [selectedEmployeeIds, setSelectedEmployeeIds] = useState([]);
  const [employeeSearch, setEmployeeSearch] = useState('');
  const [forms, setForms] = useState(null);
  const [loading, setLoading] = useState(false);
  const [detailForm, setDetailForm] = useState(null);

  const authConfig = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });

  useEffect(() => {
    (async () => {
      try {
        const [fyRes, usersRes, optRes] = await Promise.all([
          axios.get(`${API}/appraisal/fiscal-years`, authConfig()),
          axios.get(`${API}/users`, authConfig()),
          axios.get(`${API}/appraisal/admin/filter-options`, authConfig()),
        ]);
        setFiscalYears(fyRes.data);
        setFiscalYear(fyRes.data.current || '');
        setUsers(usersRes.data || []);
        setFilterOptions(optRes.data || { departments: [], projects: [] });
      } catch (e) {
        toast.error('Failed to load filters');
      }
    })();
  }, []);

  const filteredUsers = useMemo(() => {
    const q = employeeSearch.trim().toLowerCase();
    const list = [...users].sort((a, b) => (a.name || '').localeCompare(b.name || ''));
    if (!q) return list;
    return list.filter((u) =>
      (u.name || '').toLowerCase().includes(q) ||
      (u.email || '').toLowerCase().includes(q) ||
      (u.employee_id || '').toLowerCase().includes(q));
  }, [users, employeeSearch]);

  const toggleEmployee = (id) => {
    setSelectedEmployeeIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };

  const allShownSelected = filteredUsers.length > 0 && filteredUsers.every((u) => selectedEmployeeIds.includes(u.id));
  const toggleSelectAllShown = () => {
    setSelectedEmployeeIds((prev) => {
      if (allShownSelected) {
        const shown = new Set(filteredUsers.map((u) => u.id));
        return prev.filter((id) => !shown.has(id));
      }
      return [...new Set([...prev, ...filteredUsers.map((u) => u.id)])];
    });
  };

  const loadForms = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (fiscalYear && fiscalYear !== 'all') params.set('fiscal_year', fiscalYear);
      if (statusFilter !== 'all') params.set('status', statusFilter);
      if (departmentFilter !== 'all') params.set('department', departmentFilter);
      if (projectFilter !== 'all') params.set('project', projectFilter);
      if (selectedEmployeeIds.length > 0) params.set('employee_ids', selectedEmployeeIds.join(','));
      const res = await axios.get(`${API}/appraisal/admin/forms?${params.toString()}`, authConfig());
      setForms(res.data);
      if (res.data.length === 0) toast.info('No appraisal forms found for the selected filters');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load appraisal data');
    } finally {
      setLoading(false);
    }
  };

  const finalScore = (f) => (f.hr_final_score ?? f.overall_score ?? null);

  const [exporting, setExporting] = useState(false);
  const handleExport = async () => {
    setExporting(true);
    try {
      const params = new URLSearchParams();
      if (fiscalYear && fiscalYear !== 'all') params.set('fiscal_year', fiscalYear);
      if (statusFilter !== 'all') params.set('status', statusFilter);
      if (departmentFilter !== 'all') params.set('department', departmentFilter);
      if (projectFilter !== 'all') params.set('project', projectFilter);
      if (selectedEmployeeIds.length > 0) params.set('employee_ids', selectedEmployeeIds.join(','));
      const res = await axios.get(`${API}/appraisal/admin/forms/export?${params.toString()}`, {
        ...authConfig(), responseType: 'blob',
      });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `appraisal_report_${fiscalYear && fiscalYear !== 'all' ? fiscalYear : 'all'}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success('Excel downloaded');
    } catch (err) {
      let msg = 'Failed to export Excel';
      try {
        if (err.response?.data instanceof Blob) {
          msg = JSON.parse(await err.response.data.text())?.detail || msg;
        } else {
          msg = err.response?.data?.detail || msg;
        }
      } catch { /* keep generic message */ }
      toast.error(msg);
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="space-y-4" data-testid="appraisal-reports-admin">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2"><ClipboardList className="w-5 h-5" /> Appraisal Reports</CardTitle>
          <CardDescription>
            Complete appraisal data for one or many employees — self scores &amp; comments, manager review scores &amp; comments, final and HR final scores. Leave the employee list empty to include everyone.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
            <div>
              <label className="text-sm font-medium text-gray-700">Fiscal Year</label>
              <Select value={fiscalYear} onValueChange={setFiscalYear}>
                <SelectTrigger className="mt-1" data-testid="appraisal-reports-fy-select"><SelectValue placeholder="All fiscal years" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All fiscal years</SelectItem>
                  {(fiscalYears.options || []).map((fy) => <SelectItem key={fy} value={fy}>{fy}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-sm font-medium text-gray-700">Department</label>
              <Select value={departmentFilter} onValueChange={setDepartmentFilter}>
                <SelectTrigger className="mt-1" data-testid="appraisal-reports-department-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All departments</SelectItem>
                  {(filterOptions.departments || []).map((d) => <SelectItem key={d} value={d}>{d}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-sm font-medium text-gray-700">Project</label>
              <Select value={projectFilter} onValueChange={setProjectFilter}>
                <SelectTrigger className="mt-1" data-testid="appraisal-reports-project-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All projects</SelectItem>
                  {(filterOptions.projects || []).map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-sm font-medium text-gray-700">Status</label>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="mt-1" data-testid="appraisal-reports-status-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All statuses</SelectItem>
                  {Object.entries(STATUS_LABELS).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-end gap-2">
              <Button onClick={loadForms} disabled={loading} className="flex-1" data-testid="appraisal-reports-load-btn">
                <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
                {loading ? 'Loading…' : 'Load'}
              </Button>
              <Button variant="outline" onClick={handleExport} disabled={exporting} data-testid="appraisal-reports-export-btn">
                <Download className={`w-4 h-4 mr-2 ${exporting ? 'animate-bounce' : ''}`} />
                {exporting ? 'Exporting…' : 'Excel'}
              </Button>
            </div>
          </div>

          <div className="border rounded-lg">
            <div className="p-3 border-b bg-gray-50 flex items-center gap-2">
              <Search className="w-4 h-4 text-gray-400" />
              <Input
                value={employeeSearch}
                onChange={(e) => setEmployeeSearch(e.target.value)}
                placeholder="Search employees by name, email or ID…"
                className="border-0 shadow-none focus-visible:ring-0 h-8"
                data-testid="appraisal-reports-employee-search"
              />
              <span className="text-xs text-gray-500 whitespace-nowrap" data-testid="appraisal-reports-selected-count">
                {selectedEmployeeIds.length === 0 ? 'All employees' : `${selectedEmployeeIds.length} selected`}
              </span>
              <button
                type="button"
                onClick={() => setSelectedEmployeeIds([])}
                className="text-xs text-blue-600 hover:underline whitespace-nowrap"
                data-testid="appraisal-reports-clear-btn"
              >
                Clear
              </button>
            </div>
            <div className="px-2 pt-2">
              <label className="flex items-center gap-2 px-2 py-1.5 rounded hover:bg-gray-50 cursor-pointer border-b border-gray-100" data-testid="appraisal-reports-select-all">
                <Checkbox checked={allShownSelected} onCheckedChange={toggleSelectAllShown} />
                <span className="text-sm font-medium">Select all shown employees</span>
                <span className="text-xs text-gray-400">(leave all unchecked to include everyone)</span>
              </label>
            </div>
            <div className="max-h-48 overflow-y-auto p-2 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-1" data-testid="appraisal-reports-employee-list">
              {filteredUsers.map((u) => (
                <label key={u.id} className="flex items-center gap-2 px-2 py-1.5 rounded hover:bg-gray-50 cursor-pointer" data-testid={`appraisal-reports-emp-${u.employee_id || u.id}`}>
                  <Checkbox checked={selectedEmployeeIds.includes(u.id)} onCheckedChange={() => toggleEmployee(u.id)} />
                  <span className="text-sm">{u.name}</span>
                  <span className="text-xs text-gray-400">{u.employee_id || ''}</span>
                </label>
              ))}
              {filteredUsers.length === 0 && <p className="text-sm text-gray-500 p-2">No employees match.</p>}
            </div>
          </div>
        </CardContent>
      </Card>

      {forms !== null && (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Results ({forms.length})</CardTitle>
          </CardHeader>
          <CardContent>
            {forms.length === 0 ? (
              <p className="text-sm text-gray-500" data-testid="appraisal-reports-empty">No appraisal forms found.</p>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Employee</TableHead>
                      <TableHead>FY</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead className="text-right">Self Score</TableHead>
                      <TableHead className="text-right">Manager Score</TableHead>
                      <TableHead className="text-right">Final Score</TableHead>
                      <TableHead></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {forms.map((f) => (
                      <TableRow key={f.id} data-testid={`appraisal-reports-row-${f.id}`}>
                        <TableCell>
                          <div className="font-medium">{f.employee_name}</div>
                          <div className="text-xs text-gray-400">{f.employee_code || f.employee_email}</div>
                        </TableCell>
                        <TableCell>{f.fiscal_year}</TableCell>
                        <TableCell>
                          <Badge className={`${STATUS_LABELS[f.status]?.class || 'bg-gray-400'} text-white text-xs`}>
                            {STATUS_LABELS[f.status]?.label || f.status}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-right">{fmtScore(f.self_score)}</TableCell>
                        <TableCell className="text-right">{fmtScore(f.appraiser_score)}</TableCell>
                        <TableCell className="text-right font-semibold">{fmtScore(finalScore(f))}</TableCell>
                        <TableCell>
                          <Button size="sm" variant="outline" onClick={() => setDetailForm(f)} data-testid={`appraisal-reports-view-${f.id}`}>
                            <Eye className="w-4 h-4 mr-1" /> View
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      <Dialog open={!!detailForm} onOpenChange={() => setDetailForm(null)}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto" data-testid="appraisal-reports-detail-dialog">
          {detailForm && (
            <>
              <DialogHeader>
                <DialogTitle>{detailForm.employee_name} — Appraisal {detailForm.fiscal_year}</DialogTitle>
                <DialogDescription>
                  {detailForm.employee_code || detailForm.employee_email} · Manager: {detailForm.manager_name} ·{' '}
                  <Badge className={`${STATUS_LABELS[detailForm.status]?.class || 'bg-gray-400'} text-white text-xs`}>
                    {STATUS_LABELS[detailForm.status]?.label || detailForm.status}
                  </Badge>
                </DialogDescription>
              </DialogHeader>

              <div className="grid grid-cols-3 gap-3 my-2">
                <div className="p-3 rounded-lg bg-blue-50 text-center">
                  <p className="text-xs text-gray-500">Self Score</p>
                  <p className="text-xl font-bold text-blue-700" data-testid="detail-self-score">{fmtScore(detailForm.self_score)}</p>
                </div>
                <div className="p-3 rounded-lg bg-green-50 text-center">
                  <p className="text-xs text-gray-500">Manager Score</p>
                  <p className="text-xl font-bold text-green-700" data-testid="detail-manager-score">{fmtScore(detailForm.appraiser_score)}</p>
                </div>
                <div className="p-3 rounded-lg bg-purple-50 text-center">
                  <p className="text-xs text-gray-500">Final Score{detailForm.hr_final_score != null ? ' (set by HR)' : ''}</p>
                  <p className="text-xl font-bold text-purple-700" data-testid="detail-final-score">{fmtScore(finalScore(detailForm))}</p>
                  {detailForm.hr_final_score != null && (
                    <p className="text-xs text-gray-400 mt-1">by {detailForm.hr_final_score_by} · {fmtDate(detailForm.hr_final_score_at)}</p>
                  )}
                </div>
              </div>

              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>KPP</TableHead>
                    <TableHead className="text-right">Wt%</TableHead>
                    <TableHead className="text-right">Self</TableHead>
                    <TableHead>Appraisee Comment</TableHead>
                    <TableHead className="text-right">Manager</TableHead>
                    <TableHead>Manager Comment</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {(detailForm.kpp_scores || []).map((k) => (
                    <TableRow key={k.kpp_id} data-testid={`detail-kpp-${k.kpp_id}`}>
                      <TableCell>
                        <div className="font-medium text-sm">{k.kpp_name}</div>
                        <div className="text-xs text-gray-400">{k.kpp_type} · max {k.max_value}</div>
                      </TableCell>
                      <TableCell className="text-right">{k.weightage}</TableCell>
                      <TableCell className="text-right">{k.self_score ?? '—'}</TableCell>
                      <TableCell className="text-sm whitespace-pre-wrap">{k.appraisee_comment || k.self_comment || '—'}</TableCell>
                      <TableCell className="text-right">{k.appraiser_score ?? '—'}</TableCell>
                      <TableCell className="text-sm whitespace-pre-wrap">{k.appraiser_comment || '—'}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>

              <div className="space-y-2 mt-2 text-sm">
                {detailForm.final_appraisee_feedback && (
                  <div className="p-3 bg-gray-50 rounded"><strong>Employee Final Feedback:</strong> {detailForm.final_appraisee_feedback}</div>
                )}
                {detailForm.final_appraiser_feedback && (
                  <div className="p-3 bg-gray-50 rounded"><strong>Manager Final Feedback:</strong> {detailForm.final_appraiser_feedback}</div>
                )}
                {detailForm.promotion_recommendation != null && (
                  <div className="p-3 bg-gray-50 rounded">
                    <strong>Promotion Recommendation:</strong> {detailForm.promotion_recommendation ? 'Yes' : 'No'}
                    {detailForm.promotion_justification ? ` — ${detailForm.promotion_justification}` : ''}
                  </div>
                )}
                {detailForm.satisfied_with_feedback != null && (
                  <div className="p-3 bg-gray-50 rounded"><strong>Employee satisfied with feedback:</strong> {detailForm.satisfied_with_feedback ? 'Yes' : 'No'}</div>
                )}
                <div className="p-3 bg-gray-50 rounded text-xs text-gray-500">
                  Published: {fmtDate(detailForm.published_at)} · Employee submitted: {fmtDate(detailForm.employee_submitted_at)} · Manager submitted: {fmtDate(detailForm.manager_submitted_at)} · Sent to HR: {fmtDate(detailForm.sent_to_hr_at)}
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AppraisalReportsAdmin;
